import { CircularProgress } from '@mui/material';
import Box from '@mui/material/Box';
import HrefLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';

import { useResendInvitationMutation } from '@/api/v1';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';
import { User } from '@/types/user';

interface Props {
  readonly user: User;
}

export const UserStatusCell: React.FC<Props> = (props: Props): React.ReactElement => {
  const { user } = props;

  const tr = useTranslator();
  const toast = useToast();

  const [reInvite, reInviteStatus] = useResendInvitationMutation();

  const createResendCallback = React.useCallback(
    (userId: string): VoidFunction => {
      return (): void => {
        reInvite(userId);
      };
    },
    [reInvite],
  );

  React.useEffect((): VoidFunction | void => {
    if (reInviteStatus.status === QueryStatus.fulfilled) {
      toast.show(tr('RESEND_INVITE_SUCCESS'), 'success');
    } else if (reInviteStatus.status === QueryStatus.rejected) {
      toast.show(tr('RESEND_INVITE_ERROR'), 'error');
    }

    return (): void => {
      toast.remove();
    };
  }, [reInviteStatus.status, toast, tr]);

  return (
    <Box sx={styles.status}>
      <Typography variant="body1" component="span">
        {tr(user.active ? 'ACTIVE' : 'INVITATION_SENT')}
      </Typography>
      {!user.active && (
        <HrefLink sx={styles.resendLink} onClick={createResendCallback(user.id)}>
          {reInviteStatus.status === QueryStatus.pending ? (
            <CircularProgress size={16} thickness={7} />
          ) : (
            <Typography component="span" color="primary.main">
              ({tr('RESEND')})
            </Typography>
          )}
        </HrefLink>
      )}
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  status: {
    display: 'flex',
    alignItems: 'center',
    lineHeight: '24px',
    height: 24,
    gap: '1ex',
  },
  resendLink: {
    display: 'flex',
    alignItems: 'center',
    height: 24,
    lineHeight: '24px',
    textDecoration: 'none',
  },
};
